

import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from 'react';
import {
  MagnifyingGlassIcon,
  ArrowSquareOutIcon,
  FolderOpenIcon,
  CheckSquareIcon,
  BuildingsIcon,
  CommandIcon,
  XIcon,
  HandshakeIcon,
  AddressBookIcon,
  CalendarIcon,
  FileTextIcon,
  ReceiptIcon,
  TruckIcon,
  FileIcon,
  NoteIcon,
  StackIcon,
  TicketIcon,
} from '@phosphor-icons/react';
import type { FeatureFlagKeyType } from '@scrambl/shared';
import { ModuleTypeEnum, PermissionActionEnum } from '@scrambl/shared';
import { cn } from '@/lib/utils';
import { useModuleSearch } from '@/hooks/useModuleSearch';
import { ModuleSearchResult } from '@/lib/api/search';
import { useNavigate } from 'react-router-dom';
import { useOrganizationStore } from '@/store/organization.store';
import { getOrgPath } from '@/utils/org-paths';
import { useEntityTerminology } from '@/hooks/useEntityTerminology';
import { useAccountType } from '@/hooks/useAccountType';
import { usePermissions } from '@/hooks/usePermissions';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures';
import { isTauri } from '@/lib/utils/tauri';
import { useUIPreferencesStore, SIDEBAR_THEMES } from '@/store/ui-preferences.store';
import { useTheme } from '@/components/theme-provider';
import { useIsMobile } from '@/hooks/use-mobile';
import { useResolvedDarkMode } from '@/hooks/useResolvedDarkMode';
import { getTicketSearchPath } from '@/lib/search-navigation';

interface OmniSearchProps {
  className?: string;
  compact?: boolean;
}

/**
 * OmniSearch - A Stack-like global search component for the title bar
 * Features:
 * - Centered search input that acts as a drag region when not focused
 * - Global keyboard shortcut (Cmd+K / Ctrl+K)
 * - Search results dropdown with navigation
 * - Supports entities, projects, and tasks
 */
export function OmniSearch({ className, compact = false }: OmniSearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { currentOrganization } = useOrganizationStore();
  const { Singular: EntitySingular, plural: entityPlural } = useEntityTerminology();
  const { isPersonal } = useAccountType();
  const isMobile = useIsMobile();
  const { checkPermission } = usePermissions();
  const { checkFeature } = useSubscriptionFeatures();

  // Module type guard config — matches useNavigationItems.tsx
  const moduleGuards: Record<string, { featureFlag?: FeatureFlagKeyType; subscriptionFeature?: string; permission?: { module: ModuleTypeEnum; action: PermissionActionEnum } }> = {
    ENTITY: { featureFlag: 'ENTITIES' as FeatureFlagKeyType, permission: { module: ModuleTypeEnum.ENTITY, action: PermissionActionEnum.READ } },
    PROJECT: { featureFlag: 'PROJECTS' as FeatureFlagKeyType, permission: { module: ModuleTypeEnum.PROJECT, action: PermissionActionEnum.READ } },
    TASK: { featureFlag: 'TASKS' as FeatureFlagKeyType, permission: { module: ModuleTypeEnum.TASK, action: PermissionActionEnum.READ } },
    TICKET: { featureFlag: 'TICKETS' as FeatureFlagKeyType, permission: { module: ModuleTypeEnum.TICKET, action: PermissionActionEnum.READ } },
    DEAL: { featureFlag: 'DEALS' as FeatureFlagKeyType, subscriptionFeature: 'deals', permission: { module: ModuleTypeEnum.DEAL, action: PermissionActionEnum.READ } },
    CONTACT: { featureFlag: 'CONTACTS' as FeatureFlagKeyType, permission: { module: ModuleTypeEnum.CONTACT, action: PermissionActionEnum.READ } },
    EVENT: { featureFlag: 'EVENTS' as FeatureFlagKeyType, subscriptionFeature: 'events', permission: { module: ModuleTypeEnum.EVENT, action: PermissionActionEnum.READ } },
    CONTRACT: { featureFlag: 'CONTRACTS' as FeatureFlagKeyType, subscriptionFeature: 'advanced_reports', permission: { module: ModuleTypeEnum.CONTRACT, action: PermissionActionEnum.READ } },
    INVOICE: { featureFlag: 'INVOICES' as FeatureFlagKeyType, subscriptionFeature: 'advanced_reports', permission: { module: ModuleTypeEnum.INVOICE, action: PermissionActionEnum.READ } },
    SUPPLIER: {},
    NOTE: { featureFlag: 'NOTES' as FeatureFlagKeyType, permission: { module: ModuleTypeEnum.NOTE, action: PermissionActionEnum.READ } },
    DOCUMENT: { featureFlag: 'DOCUMENTS' as FeatureFlagKeyType, permission: { module: ModuleTypeEnum.DOCUMENT, action: PermissionActionEnum.READ } },
    CUSTOM_COLLECTION: { featureFlag: 'COLLECTIONS' as FeatureFlagKeyType, subscriptionFeature: 'collections', permission: { module: ModuleTypeEnum.CUSTOM_COLLECTION, action: PermissionActionEnum.READ } },
  };

  const featureKeys = useMemo(
    () => Object.values(moduleGuards).map(g => g.featureFlag).filter(Boolean) as FeatureFlagKeyType[],
    [],
  );
  const { checkFeatureFlag } = useFeatureFlags(featureKeys);
  const visibleSearchTypes = useMemo(
    () => Object.keys(moduleGuards).filter(type => {
      const guard = moduleGuards[type];
      if (!guard) return true;
      if (guard.subscriptionFeature && !checkFeature(guard.subscriptionFeature)) return false;
      if (guard.featureFlag && !checkFeatureFlag(guard.featureFlag)) return false;
      if (guard.permission && !checkPermission(guard.permission.module, guard.permission.action)) return false;
      return true;
    }),
    [checkFeature, checkFeatureFlag, checkPermission],
  );

  const isModuleVisible = (type: string) => {
    const guard = moduleGuards[type];
    if (!guard) return true;
    if (guard.subscriptionFeature && !checkFeature(guard.subscriptionFeature)) return false;
    if (guard.featureFlag && !checkFeatureFlag(guard.featureFlag)) return false;
    if (guard.permission && !checkPermission(guard.permission.module, guard.permission.action)) return false;
    return true;
  };

  // Get theme info for styling
  const { theme } = useTheme();
  const sidebarTheme = useUIPreferencesStore(state => state.sidebarTheme);

  // Determine if we're in the Tauri desktop app (uses sidebar-themed title bar)
  const isDesktopApp = isTauri() && !isMobile;

  // Stay in sync with resolved DOM theme class changes on initial load and refresh.
  const isDarkMode = useResolvedDarkMode(theme);

  // Get the current theme configuration
  const themeConfig = SIDEBAR_THEMES.find(t => t.id === sidebarTheme) || SIDEBAR_THEMES[0];

  // Determine text colors based on context:
  // - In Tauri desktop app: use sidebar theme colors
  // - In web header: use dark text on light bg (light mode) or light text on dark bg (dark mode)
  const useDarkText = isDesktopApp
    ? (isDarkMode ? false : themeConfig.lightTextColor === 'dark')
    : !isDarkMode; // Web header: dark text in light mode, light text in dark mode

  const {
    query,
    setQuery,
    groupedResults,
    isLoading,
    clearSearch,
  } = useModuleSearch({
    limit: 20,
    minSearchLength: 1,
    debounceMs: 250, // Slightly longer debounce for smoother UX
    allowedTypes: visibleSearchTypes,
  });

  // Flatten results for keyboard navigation, filtered by module visibility.
  const flatResults = (() => {
    const allResults = [
      ...groupedResults.ENTITY,
      ...groupedResults.PROJECT,
      ...groupedResults.TASK,
      ...groupedResults.TICKET,
      ...groupedResults.DEAL,
      ...groupedResults.CONTACT,
      ...groupedResults.EVENT,
      ...groupedResults.CONTRACT,
      ...groupedResults.INVOICE,
      ...groupedResults.SUPPLIER,
      ...groupedResults.NOTE,
      ...groupedResults.DOCUMENT,
      ...groupedResults.CUSTOM_COLLECTION,
    ];
    return allResults.filter(r => isModuleVisible(r.type));
  })();

  const hasResults = flatResults.length > 0;
  // Show dropdown when: open AND (has results OR has query typed)
  // Don't hide dropdown while loading - just show loading indicator with existing results
  const showDropdown = isOpen && (hasResults || query.length >= 1);

  // Get the icon for a module type
  const getModuleIcon = (type: string) => {
    switch (type) {
      case 'ENTITY':
        return <BuildingsIcon className="h-4 w-4" weight="duotone" />;
      case 'PROJECT':
        return <FolderOpenIcon className="h-4 w-4" weight="duotone" />;
      case 'TASK':
        return <CheckSquareIcon className="h-4 w-4" weight="duotone" />;
      case 'TICKET':
        return <TicketIcon className="h-4 w-4" weight="duotone" />;
      case 'DEAL':
        return <HandshakeIcon className="h-4 w-4" weight="duotone" />;
      case 'CONTACT':
        return <AddressBookIcon className="h-4 w-4" weight="duotone" />;
      case 'EVENT':
        return <CalendarIcon className="h-4 w-4" weight="duotone" />;
      case 'CONTRACT':
        return <FileTextIcon className="h-4 w-4" weight="duotone" />;
      case 'INVOICE':
        return <ReceiptIcon className="h-4 w-4" weight="duotone" />;
      case 'SUPPLIER':
        return <TruckIcon className="h-4 w-4" weight="duotone" />;
      case 'NOTE':
        return <NoteIcon className="h-4 w-4" weight="duotone" />;
      case 'DOCUMENT':
        return <FileIcon className="h-4 w-4" weight="duotone" />;
      case 'CUSTOM_COLLECTION':
        return <StackIcon className="h-4 w-4" weight="duotone" />;
      default:
        return null;
    }
  };

  // Get the path for a module
  const getModulePath = (result: ModuleSearchResult) => {
    if (!currentOrganization?.slug) return '/';

    switch (result.type) {
      case 'ENTITY':
        return getOrgPath(currentOrganization.slug, `/entities/${result.id}`);
      case 'PROJECT':
        return getOrgPath(currentOrganization.slug, `/projects/${result.id}`);
      case 'TASK':
        return getOrgPath(currentOrganization.slug, `/tasks/${result.id}`);
      case 'TICKET':
        return getTicketSearchPath(currentOrganization.slug, result);
      case 'DEAL':
        return getOrgPath(currentOrganization.slug, `/deals/${result.id}`);
      case 'CONTACT':
        return getOrgPath(currentOrganization.slug, `/contacts/${result.id}`);
      case 'EVENT':
        return getOrgPath(currentOrganization.slug, `/events/${result.id}`);
      case 'CONTRACT':
        return getOrgPath(currentOrganization.slug, `/contracts/${result.id}`);
      case 'INVOICE':
        return getOrgPath(currentOrganization.slug, `/invoices/${result.id}`);
      case 'SUPPLIER':
        return getOrgPath(currentOrganization.slug, `/suppliers/${result.id}`);
      case 'NOTE':
        return getOrgPath(currentOrganization.slug, `/notes/${result.id}`);
      case 'DOCUMENT':
        return getOrgPath(currentOrganization.slug, `/documents/${result.id}`);
      case 'CUSTOM_COLLECTION':
        // Navigate to the collection entry page using collection slug
        return getOrgPath(currentOrganization.slug, `/collections/${result.collectionSlug}/${result.id}`);
      default:
        return '/';
    }
  };

  // Navigate to selected result
  const handleSelect = (result: ModuleSearchResult) => {
    const path = getModulePath(result);
    navigate(path);
    setIsOpen(false);
    clearSearch();
    inputRef.current?.blur();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: ReactKeyboardEvent) => {
    if (!showDropdown) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < flatResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev > 0 ? prev - 1 : flatResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (flatResults[selectedIndex]) {
          handleSelect(flatResults[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        clearSearch();
        inputRef.current?.blur();
        break;
    }
  };

  // Reset selected index when results change (use flatResults length to detect actual result changes)
  useEffect(() => {
    setSelectedIndex(0);
  }, [flatResults.length]);

  // Global keyboard shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Close dropdown when clicking outside (but keep the search state for when user refocuses)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        // Don't clear search - let user come back to their results
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Dynamic text classes for the INPUT (based on sidebar theme - for title bar)
  const inputTextClasses = useDarkText ? "text-black" : "text-white";
  const inputTextMutedClasses = useDarkText ? "text-black/50" : "text-white/50";
  const inputTextSubtleClasses = useDarkText ? "text-black/40" : "text-white/40";
  const inputHoverBgClasses = useDarkText ? "hover:bg-black/10" : "hover:bg-white/10";
  const inputActiveBgClasses = useDarkText ? "bg-black/15" : "bg-white/15";
  const inputBorderClasses = useDarkText ? "border-black/20" : "border-white/20";
  const inputBgSubtleClasses = useDarkText ? "bg-black/5" : "bg-white/5";
  const inputBgHoverClasses = useDarkText ? "hover:bg-black/10 hover:border-black/10" : "hover:bg-white/10 hover:border-white/10";
  
  // Dynamic text colors for the DROPDOWN (based on dark/light mode - solid background)
  // Using inline styles to ensure they override any parent CSS rules
  const dropdownTextColor = isDarkMode ? '#ffffff' : 'hsl(var(--foreground))';
  const dropdownTextMutedColor = isDarkMode ? 'rgba(255,255,255,0.6)' : 'hsl(var(--muted-foreground))';
  const dropdownTextSubtleColor = isDarkMode ? 'rgba(255,255,255,0.4)' : 'hsl(var(--muted-foreground) / 0.7)';
  const dropdownHoverBgClasses = isDarkMode ? "hover:bg-white/10" : "hover:bg-accent";
  const dropdownActiveBgClasses = isDarkMode ? "bg-white/15" : "bg-accent";

  // Render a group of results
  const renderGroup = (title: string, results: ModuleSearchResult[], type: string) => {
    if (results.length === 0) return null;

    return (
      <div className="py-1">
        <div
          className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider"
          style={{ color: dropdownTextSubtleColor }}
        >
          {title}
        </div>
        {results.map((result) => {
          const globalIndex = flatResults.indexOf(result);
          const isSelected = globalIndex === selectedIndex;

          return (
            <button
              key={result.id}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors",
                dropdownHoverBgClasses,
                isSelected && dropdownActiveBgClasses
              )}
              style={{ color: dropdownTextColor }}
              onClick={() => handleSelect(result)}
              onMouseEnter={() => setSelectedIndex(globalIndex)}
            >
              <span className={cn(
                "flex-shrink-0",
                type === 'ENTITY' && "text-blue-500",
                type === 'PROJECT' && "text-orange-500",
                type === 'TASK' && "text-emerald-500",
                type === 'TICKET' && "text-sky-500",
                type === 'DEAL' && "text-purple-500",
                type === 'CONTACT' && "text-cyan-500",
                type === 'EVENT' && "text-amber-500",
                type === 'CONTRACT' && "text-indigo-500",
                type === 'INVOICE' && "text-lime-500",
                type === 'SUPPLIER' && "text-rose-500",
                type === 'NOTE' && "text-yellow-500",
                type === 'DOCUMENT' && "text-slate-500",
                type === 'CUSTOM_COLLECTION' && "text-violet-500",
              )}>
                {getModuleIcon(type)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {result.identifier && (
                    <span
                      className="flex-shrink-0 rounded-md border border-border/60 bg-muted/60 px-1.5 py-0.5 font-mono text-[11px] font-semibold"
                      style={{ color: dropdownTextColor }}
                    >
                      {result.identifier}
                    </span>
                  )}
                  <span className="truncate font-medium">{result.name}</span>
                </div>
                {result.parentName && (
                  <div
                    className="text-xs truncate"
                    style={{ color: dropdownTextSubtleColor }}
                  >
                    {result.parentName}
                  </div>
                )}
              </div>
              <ArrowSquareOutIcon
                className="flex-shrink-0 h-3.5 w-3.5 opacity-0 group-hover:opacity-100"
                style={{ color: dropdownTextSubtleColor }}
              />
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className={cn("relative", className)}
      // Allow parent drag region to work through this container
      data-tauri-drag-region={isDesktopApp ? true : undefined}
      style={isDesktopApp ? { WebkitAppRegion: 'drag' } as CSSProperties : undefined}
    >
      {/* Search Input Container - Acts as drag region when not focused */}
      <div
        className={cn(
          "relative flex items-center w-full",
          "rounded-lg border transition-all duration-200",
          isDesktopApp && !isFocused && "cursor-default",
          isFocused
            ? cn(inputActiveBgClasses, inputBorderClasses, useDarkText ? "ring-1 ring-black/10" : "ring-1 ring-white/10")
            : cn(
                inputBgSubtleClasses,
                useDarkText ? "border-black/30" : "border-white/30",
                inputBgHoverClasses
              )
        )}
        // Only act as drag region when not focused (for Tauri)
        data-tauri-drag-region={isDesktopApp && !isFocused ? true : undefined}
        style={isDesktopApp && !isFocused ? { WebkitAppRegion: 'drag' } as CSSProperties : undefined}
      >
        <MagnifyingGlassIcon
          className={cn(
            "absolute pointer-events-none transition-colors",
            compact ? "left-2 h-3.5 w-3.5" : "left-3 h-4 w-4",
            isFocused ? inputTextClasses : inputTextMutedClasses
          )}
          weight="bold"
        />
        <input
          ref={inputRef}
          type="text"
          spellCheck={false}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            setIsFocused(true);
            setIsOpen(true);
          }}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder="Search records..."
          className={cn(
            "w-full bg-transparent focus:outline-none",
            compact ? "h-7 pl-7 pr-14 text-xs" : "h-8 pl-9 pr-16 text-sm",
            inputTextClasses,
            useDarkText ? "placeholder:text-black/40" : "placeholder:text-white/40"
          )}
          style={{ WebkitAppRegion: 'no-drag' } as CSSProperties}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
        />
        
        {/* Keyboard shortcut hint */}
        {!isFocused && !query && (
          <div
            className={cn(
              "absolute flex items-center gap-1 pointer-events-none",
              compact ? "right-2 text-[10px]" : "right-3 text-xs",
              inputTextSubtleClasses
            )}
          >
            <kbd className={cn(
              "hidden sm:inline-flex items-center gap-0.5 rounded border font-mono font-medium h-5 px-1.5 text-[10px]",
              useDarkText
                ? "border-black/20 bg-black/5 text-black/50"
                : "border-white/20 bg-white/5 text-white/50"
            )}>
              {navigator.platform.includes('Mac') ? (
                <>
                  <CommandIcon className={compact ? "h-2 w-2" : "h-2.5 w-2.5"} weight="bold" />
                  <span>K</span>
                </>
              ) : (
                <span>Ctrl+K</span>
              )}
            </kbd>
          </div>
        )}

        {/* Clear button */}
        {query && (
          <button
            onClick={() => {
              clearSearch();
              inputRef.current?.focus();
            }}
            className={cn(
              "absolute p-0.5 rounded transition-colors right-3",
              inputHoverBgClasses,
              inputTextMutedClasses,
              useDarkText ? "hover:text-black" : "hover:text-white"
            )}
            style={{ WebkitAppRegion: 'no-drag' } as CSSProperties}
          >
            <XIcon className="h-3.5 w-3.5" weight="bold" />
          </button>
        )}
      </div>

      {/* Results Dropdown - Solid opaque background for readability */}
      {showDropdown && (
        <div
          data-omni-dropdown
          className={cn(
            "absolute top-full left-0 right-0 mt-2 z-50",
            "border rounded-lg shadow-2xl",
            "max-h-[400px] overflow-y-auto",
            "animate-in fade-in-0 slide-in-from-top-2 duration-200",
            // Use solid background colors - dark in dark mode, light card bg in light mode
            isDarkMode
              ? "bg-[#1a1a1c] border-white/10"
              : "bg-card border-border shadow-lg"
          )}
        >
          {/* Loading indicator - shown as a subtle bar at the top when loading with existing results */}
          {isLoading && hasResults && (
            <div className="h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent animate-pulse" />
          )}

          {hasResults ? (
            <div className="py-1">
              {isModuleVisible('ENTITY') && renderGroup(entityPlural, groupedResults.ENTITY, 'ENTITY')}
              {isModuleVisible('PROJECT') && renderGroup('Projects', groupedResults.PROJECT, 'PROJECT')}
              {isModuleVisible('TASK') && renderGroup('Tasks', groupedResults.TASK, 'TASK')}
              {isModuleVisible('TICKET') && renderGroup('Tickets', groupedResults.TICKET, 'TICKET')}
              {isModuleVisible('DEAL') && renderGroup('Deals', groupedResults.DEAL, 'DEAL')}
              {isModuleVisible('CONTACT') && renderGroup(isPersonal ? 'Address Book' : 'Contacts', groupedResults.CONTACT, 'CONTACT')}
              {isModuleVisible('EVENT') && renderGroup('Events', groupedResults.EVENT, 'EVENT')}
              {isModuleVisible('CONTRACT') && renderGroup('Contracts', groupedResults.CONTRACT, 'CONTRACT')}
              {isModuleVisible('INVOICE') && renderGroup('Invoices', groupedResults.INVOICE, 'INVOICE')}
              {isModuleVisible('SUPPLIER') && renderGroup('Suppliers', groupedResults.SUPPLIER, 'SUPPLIER')}
              {isModuleVisible('NOTE') && renderGroup('Notes', groupedResults.NOTE, 'NOTE')}
              {isModuleVisible('DOCUMENT') && renderGroup('Documents', groupedResults.DOCUMENT, 'DOCUMENT')}
              {isModuleVisible('CUSTOM_COLLECTION') && renderGroup('Collections', groupedResults.CUSTOM_COLLECTION, 'CUSTOM_COLLECTION')}
              {/* View all results link */}
              <button
                onClick={() => {
                  const searchPath = getOrgPath(currentOrganization?.slug || '', `/search?q=${encodeURIComponent(query)}`);
                  navigate(searchPath);
                  setIsOpen(false);
                  clearSearch();
                  inputRef.current?.blur();
                }}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium border-t transition-colors hover:opacity-80"
                style={{ color: dropdownTextMutedColor, borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'hsl(var(--border))' }}
              >
                View all results
                <ArrowSquareOutIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : isLoading ? (
            <div
              className="flex items-center justify-center py-8 text-sm"
              style={{ color: dropdownTextMutedColor }}
            >
              <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2" />
              Searching...
            </div>
          ) : query.length >= 1 ? (
            <div className="text-center">
              <div
                className="py-6 text-sm"
                style={{ color: dropdownTextMutedColor }}
              >
                No results found for &ldquo;{query}&rdquo;
              </div>
              <button
                onClick={() => {
                  const searchPath = getOrgPath(currentOrganization?.slug || '', `/search?q=${encodeURIComponent(query)}`);
                  navigate(searchPath);
                  setIsOpen(false);
                  clearSearch();
                  inputRef.current?.blur();
                }}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium border-t transition-colors hover:opacity-80"
                style={{ color: dropdownTextMutedColor, borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'hsl(var(--border))' }}
              >
                Search in full page
                <ArrowSquareOutIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
